/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import AuthInput from "@/src/components/AuthInput";
import { Button } from "@/src/components/Button/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/src/components/shad/select";
import { Label } from "@/src/components/Text/Text";
import { useCountries } from "@/src/hooks/useCountries";
import api from "@/src/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import { SelectTrigger } from "@radix-ui/react-select";
import { useMutation } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

interface Props {
  nextStep?: (e: React.SyntheticEvent) => Promise<void>;
  referral?: string;
  form: UseFormReturn<
    {
      code: string;
      email: string;
      country: string;
      surname: string;
      first_name: string;
      referral_code?: string | undefined;
      username: string;
      password: string;
      phone: string;
      source?: string | undefined;
    },
    any
  >;
}

const EmailInfo = ({ nextStep, referral, form }: Props) => {
  const { countries } = useCountries();
  const [sources, setSources] = useState<string[]>([]);
  const [seconds, setSeconds] = useState(60);
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    // getValues,
  } = form;

  const country = watch("country");

  const selectedCountry = countries?.info.find(
    (item) => item?.name === country
  );

  const reqEmail = async () => {
    const res = await api.post(apiRoutes.requestEmail, {
      email: watch("email"),
    });
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["req_email_otp"],
    mutationFn: reqEmail,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setIsActive(true);
      } else {
        toast.error(data?.info);
      }
    },
  });

  useEffect(
    function countDown() {
      if (!isActive) return;
      // Exit early when we reach 0
      if (seconds === 0) {
        setSeconds(60);
        setIsActive(false);
        return;
      }

      // Save intervalId to clear the interval when the component re-renders
      const intervalId = setInterval(() => {
        setSeconds(seconds - 1);
      }, 1000);

      // Clear interval on re-render to avoid memory leaks
      return () => clearInterval(intervalId);
    },
    [seconds, isActive]
  );

  return (
    <form onSubmit={nextStep} className="grid gap-3">
      <AuthInput
        label="Email"
        type="email"
        {...register("email")}
        errors={errors.email?.message}
      />

      <AuthInput
        {...register("code")}
        label="Verification Code"
        errors={errors.code?.message}
        // disabled={isEmpty(watch("email")) || !isEmpty(errors.email?.message)}
        right={
          <button
            disabled={
              isEmpty(watch("email")) || !isEmpty(errors.email?.message)
            }
            onClick={() => mutate()}
            className="text-xs whitespace-nowrap text-blue-500 cursor-pointer"
          >
            {isActive ? seconds : isLoading ? "Loading..." : "Get code"}
          </button>
        }
      />
      <AuthInput
        defaultValue={referral}
        {...register("referral_code")}
        label="Referral Code (optional)"
      />
      <div>
        <div>
          <Label>Country</Label>
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <div className=" grid ">
              <PopoverTrigger asChild>
                <div
                  // variant="outline"
                  role="combobox"
                  className={cn(
                    " flex justify-between text-black font-medium w-full border border-gray-200 items-center  bg-[#F6F8FF] rounded-xl px-3 py-3 md:py-4 text-sm  shadow-none  whitespace-nowrap"
                  )}
                >
                  {watch("country") ? (
                    <span className="flex items-center gap-2">
                      <img
                        src={selectedCountry?.image}
                        alt=""
                        className="h-5 w-5"
                      />
                      <span>{selectedCountry?.name}</span>
                    </span>
                  ) : (
                    <span>Select country</span>
                  )}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                </div>
              </PopoverTrigger>
              <PopoverContent
                className={cn(
                  ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                )}
                align="start"
              >
                <Command>
                  <CommandInput placeholder={`Search country`} />
                  <CommandEmpty>Nothing found.</CommandEmpty>
                  <CommandGroup>
                    {countries?.info.map((item, index) => (
                      <CommandItem
                        key={index}
                        onSelect={() => {
                          setIsOpen(false);
                          // setCountryCode(item?.short_code_iso3)
                          setValue("country", item?.name);
                          setSources(item?.sources ?? []);
                        }}
                        value={item?.name}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            watch("country") === item?.name
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />

                        <div className="flex items-center h-1">
                          <span className="text-lg mr-2">
                            <img src={item?.image} alt="" className="h-5 w-5" />
                          </span>
                          <span>{item?.name}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </div>
          </Popover>
        </div>
        {/* <Combobox
          items={
            countries?.info.map((item) => ({
              searchText: item?.name,
              value: item?.name,
              render: (
                <div key={item?.id} className="flex items-center gap-2">
                  <img src={item?.image} alt="" className="h-5 w-5" />
                  <span>{item?.name}</span>
                </div>
              ),
            })) ?? []
          }
          // emptyMessage="Nothing found."
          onChange={(val) => setValue("country", val)}
          triggerClassName=" flex justify-between text-black font-medium w-full border border-gray-200 items-center  bg-[#F6F8FF] rounded-xl px-3 py-3 md:py-4 text-sm  shadow-none  whitespace-nowrap text-black"
          defaultValue={country}
          placeholder="Select Country"
        /> */}

        {errors && (
          <span className="text-xs text-red-500">
            {errors?.country?.message}
          </span>
        )}
      </div>
      {sources?.length >= 1 && (
        <div>
          <Label>How did you hear about us?</Label>
          <Select onValueChange={(val) => setValue("source", val)}>
            <SelectTrigger className="flex justify-between text-black font-medium w-full border border-gray-200 items-center  bg-[#F6F8FF] rounded-xl px-3 py-5 md:py-6 text-sm  shadow-none  whitespace-nowrap ">
              <SelectValue placeholder="" />
            </SelectTrigger>
            <SelectContent>
              {sources?.map((item, index) => (
                <SelectItem key={index} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button
        type={"submit"}
        color="indigo"
        variant={"primary"}
        className="md:py-4 py-3 rounded-xl mt-5"
      >
        Continue
      </Button>
    </form>
  );
};

export default EmailInfo;
